import React, { useRef } from 'react';
import Markdown from 'markdown-to-jsx';
import 'katex/dist/katex.min.css';
import Latex from 'react-latex-next';
import styles from './AIMessageParser.module.scss';

interface IAIRawMessageProps {
    message: string;
    messageRef: React.RefObject<HTMLDivElement>;
}

const renderCustomMarkdown = (text: string) => {
    const latexRegex = /\\\[(.*?)\\\]|\\\((.*?)\\\)/gs;

    const parts: { type: "text" | "latex"; content: string }[] = [];
    let lastIndex = 0;

    text.replace(latexRegex, (match, ...args) => {
        const index = args[args.length - 2] as number; // Extract index from arguments
        const blockLatex = args[0]; // \[ ... \]
        const inlineLatex = args[1]; // \( ... \)

        if (index > lastIndex) {
            parts.push({ type: "text", content: text.substring(lastIndex, index) });
        }

        // Push extracted LaTeX content
        parts.push({ type: "latex", content: blockLatex || inlineLatex });

        lastIndex = index + match.length;
        return match; // Return the match to satisfy TypeScript
    });

    // append all remaining text
    if (lastIndex < text.length) {
        parts.push({ type: "text", content: text.substring(lastIndex) });
    }

    return parts;
};

const AIMessageParser: React.FC<IAIRawMessageProps> = ({ message, messageRef }) => {
    const parsedContent = renderCustomMarkdown(message);

    return (
        <div className={styles.messageContainer} ref={messageRef}>
            {parsedContent.map((part, index) =>
                part.type === 'latex' ? (
                    <div key={index} className={styles.latexBlock}>
                        <Latex>{`$${part.content}$`}</Latex>
                    </div>
                ) : (

                    <Markdown key={index}
                        options={{
                            overrides: {
                                table: {
                                    component: ExportTableToCsv,
                                },
                                a: {
                                    component: (props: any) => (
                                        <a
                                            {...props}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {props.children}
                                        </a>
                                    ),
                                },
                            },
                        }}
                    >{part.content}</Markdown>
                )
            )}
        </div>
    );
};

const ExportTableToCsv = ({ children }: { children: React.ReactNode }) => {
    const tableRef = useRef<HTMLTableElement>(null);

    const exportToCSV = () => {
        if (!tableRef.current) return;
        const rows = Array.from(tableRef.current.querySelectorAll('tr')).map(row =>
            Array.from(row.querySelectorAll('th, td')).map(cell =>
                `"${cell.textContent?.replace(/"/g, '""') || ''}"`
            ).join(',')
        );

        const csv = rows.join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'table-export.csv';
        a.click();
    };

    return (
        <div className={styles.exportWrapper}>
            <div role="none" onClick={exportToCSV} title="Export to CSV" className={styles.exportIcon}>
                📤
            </div>
            <table ref={tableRef} className={styles.tableStyles}>
                {children}
            </table>
        </div>
    );
};

export default AIMessageParser;
